/**
 * CommandPanel — issue and view gateway commands.
 * Permission model:
 * - Diagnostic/lifecycle: always available to installer
 * - Protective (write-gated): requires writes_enabled
 * - Commissioning-only: requires lifecycle_status = 'commissioning'
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Terminal, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useGatewayCommands } from '@/hooks/useGatewayCommands';
import type { GatewayDevice, GatewayCommand, CommandDefinition } from '@/types/gateway';
import { GATEWAY_COMMANDS, COMMAND_STATUS_CONFIG } from '@/types/gateway';
import { toast } from 'sonner';

interface CommandPanelProps {
  device: GatewayDevice;
  onDeviceUpdate: () => void;
}

export function CommandPanel({ device, onDeviceUpdate }: CommandPanelProps) {
  const { commands, isLoading, issueCommand, isIssuing } = useGatewayCommands(device.id);
  const [selectedCommand, setSelectedCommand] = useState('');
  const [confirmDef, setConfirmDef] = useState<CommandDefinition | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [writesToggling, setWritesToggling] = useState(false);

  // Filter available commands based on device state
  const availableCommands = useMemo(() => {
    return GATEWAY_COMMANDS.filter((cmd) => {
      if (cmd.requiresWritesEnabled && !device.writes_enabled) return false;
      if (cmd.commissioningOnly && device.lifecycle_status !== 'commissioning') return false;
      return true;
    });
  }, [device.writes_enabled, device.lifecycle_status]);

  const handleIssueCommand = async (commandType: string) => {
    try {
      await issueCommand({ commandType });
      toast.success('Command queued. Device will execute within 1 minute.');
      setSelectedCommand('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to issue command');
    }
  };

  const handleCommandSelect = (cmdType: string) => {
    const def = GATEWAY_COMMANDS.find((c) => c.type === cmdType);
    if (!def) return;

    if (def.criticality === 'high' && def.confirmationText) {
      setConfirmDef(def);
    } else {
      handleIssueCommand(cmdType);
    }
  };

  const handleWritesToggle = async (enabled: boolean) => {
    setWritesToggling(true);
    try {
      const { error } = await supabase
        .from('gateway_devices')
        .update({ writes_enabled: enabled })
        .eq('id', device.id);
      if (error) throw new Error(error.message);
      onDeviceUpdate();
      toast.success(enabled ? 'Equipment commands enabled' : 'Equipment commands disabled');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to toggle writes');
    } finally {
      setWritesToggling(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Writes toggle */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-wt-text-primary">Enable Equipment Commands</p>
            <p className="text-xs text-wt-text-secondary">
              Allows the gateway to write settings to inverters and batteries
            </p>
          </div>
          <Switch
            checked={device.writes_enabled}
            onCheckedChange={handleWritesToggle}
            disabled={writesToggling}
          />
        </CardContent>
      </Card>

      {/* Issue command */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            Issue Command
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Select value={selectedCommand} onValueChange={setSelectedCommand}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select command..." />
              </SelectTrigger>
              <SelectContent>
                {availableCommands.map((cmd) => (
                  <SelectItem key={cmd.type} value={cmd.type}>
                    {cmd.label}
                    {cmd.criticality === 'high' && ' (!!)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => handleCommandSelect(selectedCommand)}
              disabled={!selectedCommand || isIssuing}
            >
              {isIssuing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Issue'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Command history */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary">
            Command History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
            </div>
          ) : commands.length === 0 ? (
            <p className="py-6 text-center text-sm text-wt-text-secondary">
              No commands have been issued for this gateway.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-wt-text-secondary text-left">
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Created</th>
                    <th className="px-4 py-2 font-medium">Executed</th>
                    <th className="px-4 py-2 font-medium w-8" />
                  </tr>
                </thead>
                <tbody>
                  {commands.map((cmd) => (
                    <CommandRow
                      key={cmd.id}
                      command={cmd}
                      expanded={expandedId === cmd.id}
                      onToggle={() => setExpandedId(expandedId === cmd.id ? null : cmd.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog for high-criticality commands */}
      <AlertDialog open={!!confirmDef} onOpenChange={() => setConfirmDef(null)}>
        <AlertDialogContent className="bg-wt-bg-secondary border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-wt-text-primary">{confirmDef?.label}</AlertDialogTitle>
            <AlertDialogDescription className="text-wt-text-secondary">
              {confirmDef?.confirmationText}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDef) handleIssueCommand(confirmDef.type);
                setConfirmDef(null);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CommandRow
// ---------------------------------------------------------------------------

function CommandRow({
  command,
  expanded,
  onToggle,
}: {
  command: GatewayCommand;
  expanded: boolean;
  onToggle: () => void;
}) {
  const statusConfig = COMMAND_STATUS_CONFIG[command.status];

  return (
    <>
      <tr
        className="border-b border-border/50 hover:bg-wt-bg-tertiary/50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-2 text-wt-text-primary">{command.command_type}</td>
        <td className="px-4 py-2 text-wt-text-secondary capitalize">{command.category}</td>
        <td className="px-4 py-2">
          <Badge variant="outline" className={statusConfig.color}>
            {statusConfig.label}
          </Badge>
        </td>
        <td className="px-4 py-2 text-wt-text-secondary">
          {formatDistanceToNow(new Date(command.created_at), { addSuffix: true })}
        </td>
        <td className="px-4 py-2 text-wt-text-secondary">
          {command.executed_at
            ? formatDistanceToNow(new Date(command.executed_at), { addSuffix: true })
            : '--'}
        </td>
        <td className="px-4 py-2">
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-wt-text-secondary" />
          ) : (
            <ChevronDown className="w-4 h-4 text-wt-text-secondary" />
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-wt-bg-tertiary/30">
          <td colSpan={6} className="px-4 py-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-wt-text-secondary">Sequence ID</span>
              <span className="font-mono-numeric">{command.sequence_id}</span>
              <span className="text-wt-text-secondary">Criticality</span>
              <span className="capitalize">{command.criticality}</span>
              <span className="text-wt-text-secondary">TTL</span>
              <span className="font-mono-numeric">{command.ttl_seconds}s</span>
              <span className="text-wt-text-secondary">Created</span>
              <span>{format(new Date(command.created_at), 'yyyy-MM-dd HH:mm:ss')}</span>
              {command.delivered_at && (
                <>
                  <span className="text-wt-text-secondary">Delivered</span>
                  <span>{format(new Date(command.delivered_at), 'yyyy-MM-dd HH:mm:ss')}</span>
                </>
              )}
              {command.audit_notes && (
                <>
                  <span className="text-wt-text-secondary">Notes</span>
                  <span>{command.audit_notes}</span>
                </>
              )}
            </div>
            {command.payload && Object.keys(command.payload).length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-wt-text-secondary mb-1">Payload</p>
                <pre className="text-xs font-mono-numeric bg-wt-bg-primary rounded p-2 overflow-auto max-h-32">
                  {JSON.stringify(command.payload, null, 2)}
                </pre>
              </div>
            )}
            {command.result && Object.keys(command.result).length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-wt-text-secondary mb-1">Result</p>
                <pre className="text-xs font-mono-numeric bg-wt-bg-primary rounded p-2 overflow-auto max-h-32">
                  {JSON.stringify(command.result, null, 2)}
                </pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
